import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Users as UsersIcon, Stethoscope, Newspaper, ShoppingBag, Receipt,
  TrendingUp, Banknote, ArrowRight, CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/common/PageHeader";
import { UserName } from "@/components/common/EntityName";
import {
  usersApi, doctorsApi, postsApi, productsApi,
  paymentsApi, revenueApi, withdrawalsApi,
} from "@/lib/api";
import type { Doctor, DoctorRevenue } from "@/lib/api/types";
import { fmtCurrency, fmtNumber, fmtRelativeTime } from "@/lib/utils";
import { useAuthStore } from "@/lib/auth/store";

export default function Dashboard() {
  const session = useAuthStore((s) => s.session);

  // Lightweight totals — request size:1 so we only pay for the count.
  const users = useQuery({
    queryKey: ["dash", "users"],
    queryFn: () => usersApi.list({ size: 1 }).then((r) => r.data.data?.totalItems ?? 0),
  });
  const doctors = useQuery({
    queryKey: ["dash", "doctors"],
    queryFn: () => doctorsApi.list({ size: 1 }).then((r) => r.data.data?.totalItems ?? 0),
  });
  const doctorsPending = useQuery({
    queryKey: ["dash", "doctors-pending"],
    queryFn: () => doctorsApi.listPending({ size: 1 }).then((r) => r.data.data?.totalItems ?? 0),
  });
  const posts = useQuery({
    queryKey: ["dash", "posts"],
    queryFn: () => postsApi.adminList({ size: 1 }).then((r) => r.data.data?.totalItems ?? 0),
  });
  const products = useQuery({
    queryKey: ["dash", "products"],
    queryFn: () => productsApi.list({ size: 1 }).then((r) => r.data.data?.totalItems ?? 0),
  });
  const paySummary = useQuery({
    queryKey: ["dash", "pay-summary"],
    queryFn: () => paymentsApi.summary().then((r) => r.data.data),
  });
  const platform = useQuery({
    queryKey: ["dash", "platform-revenue"],
    queryFn: () => revenueApi.platform().then((r) => r.data.data),
  });
  const withdrawalsPending = useQuery({
    queryKey: ["dash", "withdrawals-pending"],
    queryFn: () => withdrawalsApi.pendingCount().then((r) => r.data.data ?? 0),
  });
  const topDoctors = useQuery({
    queryKey: ["dash", "top-doctors"],
    queryFn: () => revenueApi.topDoctors(6).then((r) => r.data.data ?? []),
  });
  const pendingList = useQuery({
    queryKey: ["dash", "pending-doctors-list"],
    queryFn: () => doctorsApi.listPending({ size: 4 }).then((r) => r.data.data?.items ?? []),
  });
  const recentPosts = useQuery({
    queryKey: ["dash", "recent-posts"],
    queryFn: () => postsApi.adminList({ size: 6 }).then((r) => r.data.data?.items ?? []),
  });

  const stats = [
    { to: "/users", label: "Users", icon: UsersIcon, value: users.data, hint: "All registered accounts", alert: false, money: false },
    { to: "/doctors", label: "Doctors", icon: Stethoscope, value: doctors.data, hint: `${doctorsPending.data ?? 0} awaiting review`, alert: (doctorsPending.data ?? 0) > 0, money: false },
    { to: "/posts", label: "Posts", icon: Newspaper, value: posts.data, hint: "Feed content (all states)", alert: false, money: false },
    { to: "/products", label: "Products", icon: ShoppingBag, value: products.data, hint: "Marketplace items", alert: false, money: false },
    { to: "/transactions", label: "Transactions", icon: Receipt, value: paySummary.data?.totalCount, hint: `${fmtCurrency(paySummary.data?.completedAmount ?? 0, "TZS")} collected`, alert: false, money: false },
    { to: "/revenue", label: "Commission", icon: TrendingUp, value: platform.data ? fmtCurrency(platform.data.totalCommission, "TZS") : undefined, hint: `${platform.data?.count ?? 0} commission records`, alert: false, money: true },
  ];

  return (
    <>
      <PageHeader
        title={`Operations Dashboard${session?.name ? ` — ${session.name}` : ""}`}
        subtitle="Live view of people, content and money across Afya24."
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, delay: i * 0.04 }}
          >
            <Link to={s.to}>
              <Card className={`h-full transition hover:shadow-md hover:border-primary/40 ${s.alert ? "border-warning/60" : ""}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {s.label}
                    </span>
                    <s.icon className={`size-4 ${s.alert ? "text-warning" : "text-primary"}`} />
                  </div>
                  <div className={`mt-2 font-extrabold ${s.money ? "text-lg" : "text-2xl"}`}>
                    {s.value === undefined ? "…" : typeof s.value === "number" ? fmtNumber(s.value) : s.value}
                  </div>
                  <div className="mt-1 truncate text-[11px] font-semibold text-muted-foreground">{s.hint}</div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        {/* Top earning doctors */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Top earning doctors</CardTitle>
            <CardDescription>Doctor take-home after platform commission — see Revenue for the full report.</CardDescription>
          </CardHeader>
          <CardContent>
            {topDoctors.data && topDoctors.data.length > 0 ? (
              <div className="space-y-3">
                {topDoctors.data.map((d: DoctorRevenue, i: number) => {
                  const max = Math.max(...topDoctors.data!.map((x) => x.totalEarnings), 1);
                  return (
                    <div key={d.doctorId} className="flex items-center gap-3">
                      <span className="w-5 text-right text-xs font-bold text-muted-foreground">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="truncate font-semibold"><UserName id={d.doctorId} /></span>
                          <span className="shrink-0 font-bold">{fmtCurrency(d.totalEarnings, "TZS")}</span>
                        </div>
                        <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-gradient-sunset"
                            style={{ width: `${Math.max(4, Math.round((d.totalEarnings / max) * 100))}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No commission records yet — earnings appear after paid consultations.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Queues */}
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Approvals queue</CardTitle>
              <CardDescription>Doctors waiting for verification.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {pendingList.data && pendingList.data.length > 0 ? (
                <>
                  {pendingList.data.map((d: Doctor) => (
                    <Link key={d.id} to="/doctors" className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm transition hover:bg-muted/50">
                      <span className="min-w-0">
                        <span className="block truncate font-semibold">{d.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">{d.specialization ?? "—"}</span>
                      </span>
                      <Badge variant="warning">Pending</Badge>
                    </Link>
                  ))}
                  <Link to="/doctors" className="flex items-center gap-1 pt-1 text-xs font-semibold text-primary">
                    Review all <ArrowRight className="size-3" />
                  </Link>
                </>
              ) : (
                <p className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
                  <CheckCircle2 className="size-4 text-success" /> All clear — no pending doctors.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Finance queue</CardTitle>
              <CardDescription>Money movements needing action.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/withdrawals" className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm transition hover:bg-muted/50">
                <span className="flex items-center gap-2 font-semibold"><Banknote className="size-4 text-muted-foreground" /> Withdrawals pending</span>
                <Badge variant={(withdrawalsPending.data ?? 0) > 0 ? "warning" : "muted"}>
                  {withdrawalsPending.data ?? 0}
                </Badge>
              </Link>
              <Link to="/transactions" className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm transition hover:bg-muted/50">
                <span className="flex items-center gap-2 font-semibold"><Receipt className="size-4 text-muted-foreground" /> Failed payments</span>
                <Badge variant={(paySummary.data?.failedCount ?? 0) > 0 ? "destructive" : "muted"}>
                  {paySummary.data?.failedCount ?? 0}
                </Badge>
              </Link>
              <Link to="/transactions" className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm transition hover:bg-muted/50">
                <span className="flex items-center gap-2 font-semibold"><Receipt className="size-4 text-muted-foreground" /> Payments in flight</span>
                <Badge variant="muted">{paySummary.data?.pendingCount ?? 0}</Badge>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent posts */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Most recent posts</CardTitle>
          <CardDescription>Latest content across the feed, including drafts and moderated posts.</CardDescription>
        </CardHeader>
        <CardContent>
          {recentPosts.data && recentPosts.data.length > 0 ? (
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {recentPosts.data.map((p) => (
                <Link key={p.id} to="/posts" className="rounded-lg border border-border p-3 transition hover:bg-muted/50">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-semibold">{p.title}</p>
                    {p.takenDown ? (
                      <Badge variant="destructive">Taken down</Badge>
                    ) : p.isDraft ? (
                      <Badge variant="muted">Draft</Badge>
                    ) : (
                      <Badge variant="success">Live</Badge>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {p.category ?? "General"} · {fmtRelativeTime(p.createdAt)}
                  </p>
                </Link>
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-muted-foreground">No posts yet.</p>
          )}
        </CardContent>
      </Card>
    </>
  );
}
